'use client'

import { useState } from 'react'

import { track } from '@amplitude/analytics-browser'
import { Search } from 'lucide-react'
import useSWR from 'swr'
import { z } from 'zod'

import { buildDepartUrl } from '@/lib/c2c/build-depart-url'
import type { C2CClientTemplates } from '@/lib/c2c/copy-declaration'
import { buildC2CTripSelection } from '@/lib/c2c/ecommerce-items'
import { TripDataSchema, type TripData } from '@/lib/c2c/transforms/schedule'
import { CANONICAL_CURRENCY, type DisplayCurrency } from '@/lib/currency/map'
import { useLogger } from '@/hooks/use-logger'
import { CrawlSafeLink } from '@/components/crawl-safe-link'
import { reportEcommerceEvent } from '@/services/gtm'

import { DateSelector, shiftDate, type DateSelectorCopy } from './date-selector'
import { TripCard } from './trip-card'

/**
 * Amplitude `Trip Selected` payload for a c2c-page trip-card click.
 *
 * c2c is a pre-search surface — the convention's canonical `trips` shape
 * adds `depart_city_code`, `arrive_city_code`, `depart_wid`, `arrive_wid`,
 * `leg_count`, `f_pricing`, and `total_cart_price_usd` from search results
 * onward. Those fields are omitted here because they are not derivable on
 * c2c. The four city/wid codes are encoded inside `trip_id` for post-hoc
 * decoding by analysts. See FR-C2 in
 * .docs/specs/legacy/product/c2c/amplitude-parity-audit/spec.md.
 */
function buildTripSelectedPayload(
    trip: TripData,
    mode: string,
    position: number,
): Record<string, unknown> {
    const tripObject: Record<string, unknown> = {
        trip_id: trip.tripId,
        mode,
        is_routed: trip.transfers > 0,
        transfer_count: trip.transfers,
    }
    if (trip.carrierCode !== undefined) {
        tripObject.carrier_code = trip.carrierCode
    }
    // The CANONICAL fare, not the rendered one. Once the page prices in pesos those are
    // different numbers, and a peso amount published under a `_usd` name would corrupt
    // every downstream revenue figure rather than merely mislabel one event.
    if (trip.priceUsd !== undefined) {
        tripObject.ticket_price_usd = Math.round(trip.priceUsd * 100) / 100
    }
    return {
        trips: [tripObject],
        results_position: position,
    }
}

interface LocationData {
    placeId: string
    name: string
    lat: number
    lng: number
    wcityid: string
}

/**
 * The store keys this section's chrome renders. `timeTableTitle` interpolates the
 * city names and mode word itself, so the heading arrives fully rendered — the
 * component builds no copy of its own.
 */
type UpcomingTripsLabelKey =
    | 'timeTableTitle'
    | 'timeTableNotFound'
    | 'timeTableSeeMore'
    // Passed straight through to each TripCard's chrome — see TripCardLabelKey.
    | 'luxury'
    | 'transModeLabel'
    | 'stars'

interface UpcomingTripsProps {
    /** The date strip's scroll-control labels, the shell's copy. */
    dateSelectorCopy: DateSelectorCopy
    /** Heading, empty state and CTA label, keyed by the translation-store key each carries. */
    labels?: Partial<Record<UpcomingTripsLabelKey, string>>
    /** Each card's fare, clocks, duration and transfer count, as client templates. */
    templates: C2CClientTemplates
    origin: LocationData
    destination: LocationData
    initialDate?: string
    locale: string
    /**
     * The currency the page stated its prices in — the rendered one, not the locale's
     * default. Travels to the schedules API so a date change comes back in the same
     * currency as the prices already on the page. Defaults to
     * canonical so a caller that does not thread it keeps its dollars labelled as dollars.
     */
    currency?: DisplayCurrency
    mode: string
    initialTrips?: TripData[]
    /**
     * Server-computed "today" (YYYY-MM-DD) anchoring the schedule dates. Required
     * (not optional with an ambient fallback) so a caller can't silently reintroduce
     * a render-time `new Date()` read — invariants.md#ssr-safe-rendered-dates.
     */
    baseDate: string
}

const SchedulesResponseSchema = z.object({ trips: z.array(z.unknown()) })

/** The schedules API's trips, each minted on its own: one the card cannot state is left
 *  out, as the server leaves it out, and the rest render. */
async function fetchTrips(url: string, isSeoPage: boolean): Promise<{ trips: TripData[] }> {
    const response = await fetch(`${url}&isSeoPage=${isSeoPage}`)
    if (!response.ok) {
        throw new Error('Failed to fetch trips')
    }
    const { trips } = SchedulesResponseSchema.parse(await response.json())
    return {
        trips: trips.flatMap((trip) => {
            const minted = TripDataSchema.safeParse(trip)
            return minted.success ? [minted.data] : []
        }),
    }
}

export function UpcomingTrips({
    dateSelectorCopy,
    labels,
    templates,
    origin,
    destination,
    initialDate,
    locale,
    currency = CANONICAL_CURRENCY,
    mode,
    initialTrips,
    baseDate,
}: UpcomingTripsProps) {
    const logger = useLogger({ module: 'upcoming-trips' })
    const DAYS_TO_SHOW = 7
    const tomorrow = shiftDate(baseDate, 1)
    const fixedDates = Array.from({ length: DAYS_TO_SHOW }, (_, i) => shiftDate(tomorrow, i))

    const [selectedDate, setSelectedDate] = useState(() => {
        if (!initialDate || initialDate <= tomorrow) {
            return tomorrow
        }
        return initialDate
    })

    // Track whether the user has changed dates — the initial load uses isSeoPage=true
    // (cached data), a date change uses isSeoPage=false (real-time data). isSeoPage is
    // excluded from the SWR key so revisiting a previously-fetched date serves the
    // cached result.
    const [hasChangedDate, setHasChangedDate] = useState(false)

    function handleDateChange(date: string) {
        if (date === selectedDate) {
            return
        }
        setHasChangedDate(true)
        setSelectedDate(date)
    }

    const params = new URLSearchParams({
        originPlaceId: origin.placeId,
        originName: origin.name,
        originLat: String(origin.lat),
        originLng: String(origin.lng),
        destPlaceId: destination.placeId,
        destName: destination.name,
        destLat: String(destination.lat),
        destLng: String(destination.lng),
        fromId: origin.wcityid,
        toId: destination.wcityid,
        date: selectedDate,
        locale,
        currency,
        mode,
    })

    const hasRequiredData = origin.placeId && destination.placeId
    const hasServerData = initialTrips !== undefined && !hasChangedDate

    // SWR key is null when using server data — no fetch on mount
    const swrKey = hasServerData
        ? null
        : hasRequiredData
          ? `/api/nexus/schedules?${params.toString()}`
          : null

    const { data, error, isLoading } = useSWR<{ trips: TripData[] }, Error>(
        swrKey,
        (url: string) => fetchTrips(url, !hasChangedDate),
        { revalidateOnFocus: false, revalidateIfStale: false },
    )

    // Upstream failures (e.g. 502 from /api/nexus/schedules) surface as SWR
    // errors. Collapse them into the "no preview" state so the page keeps
    // rendering the empty experience instead of a loud error card.
    const trips = hasServerData ? initialTrips : error ? [] : data?.trips
    const showLoading = !hasServerData && isLoading
    const hasData = hasServerData || data !== undefined || Boolean(error)

    if (!hasRequiredData) {
        return null
    }

    const searchUrl = buildDepartUrl({
        origin: origin.name,
        destination: destination.name,
        date: selectedDate,
        locale,
        query: { dpid: destination.placeId, opid: origin.placeId },
    })

    return (
        <section
            id="schedule"
            data-section="upcoming-trips"
            className="my-[72px] md:mx-auto md:my-24 md:flex md:max-w-[637px] md:flex-col md:justify-center"
        >
            {labels?.timeTableTitle && (
                <h2 className="mb-6 text-center text-[20px] font-bold text-[#44474a] md:mb-0 md:text-[32px]">
                    {labels.timeTableTitle}
                </h2>
            )}

            <DateSelector
                dates={fixedDates}
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                locale={locale}
                {...dateSelectorCopy}
            />

            <div className="mt-4 space-y-2">
                {showLoading && (
                    <div className="flex items-center justify-center gap-2 py-16">
                        <span
                            data-testid="loading-dot"
                            className="h-3 w-3 animate-pulse rounded-full bg-[var(--watermelon-500)]"
                        />
                        <span
                            data-testid="loading-dot"
                            className="h-3 w-3 animate-pulse rounded-full bg-[var(--wanderblu-500)]"
                        />
                        <span
                            data-testid="loading-dot"
                            className="h-3 w-3 animate-pulse rounded-full bg-[var(--wanderblu-700)]"
                        />
                    </div>
                )}

                {trips?.map((trip, index) => (
                    <TripCard
                        key={trip.tripId}
                        departTime={trip.departTime}
                        arriveTime={trip.arriveTime}
                        departStation={trip.departStation}
                        arriveStation={trip.arriveStation}
                        duration={trip.duration}
                        fare={trip.fare}
                        carrierName={trip.carrierName}
                        carrierLogo={trip.carrierLogo}
                        carrierRating={trip.carrierRating}
                        isLuxury={trip.isLuxury}
                        isNextGen={trip.isNextGen}
                        searchUrl={trip.searchUrl}
                        transfers={trip.transfers}
                        mode={mode}
                        templates={templates}
                        labels={{
                            luxury: labels?.luxury,
                            transModeLabel: labels?.transModeLabel,
                            stars: labels?.stars,
                        }}
                        onSelect={() => {
                            // Both reports go out before CrawlSafeLink navigates: that is a
                            // full page load, and a message queued after it never ships.
                            // Neither may cost the visitor that navigation, so the tracker
                            // that can throw is contained — the queue push resolves an
                            // outcome instead.
                            try {
                                track('Trip Selected', buildTripSelectedPayload(trip, mode, index))
                            } catch {
                                // A tracker that cannot record is no reason to strand a click
                                // on a control the visitor took for a link.
                            }
                            const selection = buildC2CTripSelection({
                                departWcityid: origin.wcityid,
                                arriveWcityid: destination.wcityid,
                                mode,
                                tripId: trip.tripId,
                                carrierCode: trip.carrierCode,
                                priceUsd: trip.priceUsd,
                                position: index,
                                departDate: selectedDate,
                            })
                            if (selection) {
                                const outcome = reportEcommerceEvent('select_item', selection)
                                if (outcome === 'failed') {
                                    logger.warn('c2c select_item not reported')
                                }
                            }
                        }}
                    />
                ))}

                {/* The magnifier alone says nothing, so the empty state goes with its copy. */}
                {trips?.length === 0 && labels?.timeTableNotFound && (
                    <div className="flex flex-col items-center py-10 text-center md:mx-auto md:w-1/2">
                        <Search className="mb-6 h-10 w-10 text-[#767676]" />
                        <span className="text-[#767676]">{labels.timeTableNotFound}</span>
                    </div>
                )}
            </div>

            {/* An unlabeled button is unusable, so the control goes with its label. */}
            {hasData && labels?.timeTableSeeMore && (
                <CrawlSafeLink
                    url={searchUrl}
                    className="mx-auto mt-6 block w-full cursor-pointer rounded-[7px] bg-[#d13b3b] py-3 text-center text-sm font-bold tracking-wide text-white uppercase transition-colors hover:bg-[#b83232] md:mt-12 md:w-[280px]"
                >
                    {labels.timeTableSeeMore}
                </CrawlSafeLink>
            )}
        </section>
    )
}
